<!-- eslint-disable vue/no-setup-props-destructure -->
<script setup lang="ts">
import { DateTime } from 'luxon'
import type { GeoFenceVisitWithObjectsLinked } from '~/services/apiClientGps'
import CompletedIcon from '~/components/icons/CompletedIcon.vue'
import InProgressIcon from '~/components/icons/InProgressIcon.vue'
import TruckIcon from '~/components/icons/TruckIcon.vue'
import LoadedContainerIcon from '~/components/icons/LoadedContainerIcon.vue'
import EmptyContainerIcon from '~/components/icons/EmptyContainerIcon.vue'
import { calculateDuration, formatTime } from '~/utils/dateTimeUtils'
import { getTerminalLabel } from '~/constants'
import type { TerminalName } from '~/services/apiClient'

const props = defineProps<{
  visit: GeoFenceVisitWithObjectsLinked
  isSelected: boolean
  index: number
  totalVisits: number
}>()

const emit = defineEmits<{
  (e: 'select', visit: GeoFenceVisitWithObjectsLinked): void
}>()

// Extract appointments and gate transactions from container moves
const extractedAppointments = computed(() => {
  if (!props.visit?.transaction) return []

  const appointments = []

  // Extract from inbound container moves
  if (props.visit.transaction.inbound_container_moves) {
    for (const move of props.visit.transaction.inbound_container_moves) {
      if (move.appointment) {
        appointments.push(move.appointment)
      }
    }
  }

  // Extract from outbound container moves
  if (props.visit.transaction.outbound_container_moves) {
    for (const move of props.visit.transaction.outbound_container_moves) {
      if (move.appointment) {
        appointments.push(move.appointment)
      }
    }
  }

  return appointments
})

const extractedGateTransactions = computed(() => {
  if (!props.visit?.transaction) return []

  const gateTransactions = []

  // Extract from inbound container moves
  if (props.visit.transaction.inbound_container_moves) {
    for (const move of props.visit.transaction.inbound_container_moves) {
      if (move.gate_transaction) {
        gateTransactions.push(move.gate_transaction)
      }
    }
  }

  // Extract from outbound container moves
  if (props.visit.transaction.outbound_container_moves) {
    for (const move of props.visit.transaction.outbound_container_moves) {
      if (move.gate_transaction) {
        gateTransactions.push(move.gate_transaction)
      }
    }
  }

  return gateTransactions
})

// Extract container information from moves
const extractedContainers = computed(() => {
  if (!props.visit?.transaction) return []

  const containers = []

  // Extract from inbound container moves
  if (props.visit.transaction.inbound_container_moves) {
    for (const move of props.visit.transaction.inbound_container_moves) {
      containers.push({
        number: move.container_number,
        direction: move.direction,
        loaded: move.loaded,
        type: 'move' as const,
      })
    }
  }

  // Extract from outbound container moves
  if (props.visit.transaction.outbound_container_moves) {
    for (const move of props.visit.transaction.outbound_container_moves) {
      containers.push({
        number: move.container_number,
        direction: move.direction,
        loaded: move.loaded,
        type: 'move' as const,
      })
    }
  }

  // Also extract from appointments if no moves found
  if (containers.length === 0) {
    for (const appointment of extractedAppointments.value) {
      containers.push({
        number: appointment.container_number,
        direction: appointment.direction,
        loaded: appointment.loaded,
        type: 'appointment' as const,
      })
    }
  }

  // Sort containers: empty ones first, then loaded ones
  return containers.sort((a, b) => {
    if (a.loaded === b.loaded) return 0
    return a.loaded ? 1 : -1
  })
})

// Separate containers by type
const emptyContainers = computed(() => {
  return extractedContainers.value.filter((container) => !container.loaded)
})

const loadedContainers = computed(() => {
  return extractedContainers.value.filter((container) => container.loaded)
})

const hasEmptyContainers = computed(() => emptyContainers.value.length > 0)
const hasLoadedContainers = computed(() => loadedContainers.value.length > 0)

const hasMissingTransaction = computed(() => {
  return (
    extractedAppointments.value.length === 0 &&
    extractedGateTransactions.value.length === 0
  )
})

// Constants
const DURATION_THRESHOLDS_HOURS = {
  NORMAL_MAX: 1.5, // Up to 1.5 hours is normal
  WARNING_MAX: 2, // Up to 2 hours is a warning
  // Anything above WARNING_MAX would be critical
} as const

const DURATION_COLOR_CLASSES = {
  NORMAL: 'text-black',
  WARNING: 'text-orange-500',
  CRITICAL: 'text-red-500',
} as const

// Function to get duration color based on visit duration
const getDurationColor = (visit: GeoFenceVisitWithObjectsLinked): string => {
  if (!visit.arrival) return 'text-black'

  const arrivalTime = new Date(visit.arrival).getTime()
  const endTime = visit.departure
    ? new Date(visit.departure).getTime()
    : Date.now()

  const durationMs = endTime - arrivalTime
  const durationHours = durationMs / (1000 * 60 * 60) // Convert to hours

  if (durationHours < DURATION_THRESHOLDS_HOURS.NORMAL_MAX) {
    return DURATION_COLOR_CLASSES.NORMAL
  } else if (durationHours < DURATION_THRESHOLDS_HOURS.WARNING_MAX) {
    return DURATION_COLOR_CLASSES.WARNING
  } else {
    return DURATION_COLOR_CLASSES.CRITICAL
  }
}
</script>

<template>
  <div
    class="cursor-pointer hover:bg-gray-50 transition-colors"
    :class="[
      {
        'bg-blue-100 hover:bg-blue-100': isSelected,
      },
      { 'border-b border-gray-100': index !== totalVisits - 1 },
    ]"
    @click="emit('select', props.visit)"
  >
    <div class="p-2">
      <!-- First Row -->
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-1">
          <span class="font-bold">
            {{
              getTerminalLabel(
                props.visit.geo_fence?.location?.name as TerminalName
              )
            }}
          </span>
          <span class="text-sm flex items-center space-x-1">
            <span>•</span>
            <span
              v-tooltip="`Device Name: ${props.visit.tracking_device?.name}`"
              class="text-gray-400"
            >
              {{ props.visit.tracking_device?.name }}
            </span>
            <template v-if="props.visit.tracking_device?.drivers?.[0]?.name">
              <span>•</span>
              <span
                v-tooltip="
                  `Driver Name: ${props.visit.tracking_device?.drivers?.[0]?.name}`
                "
                class="truncate max-w-[200px] text-gray-500 text-xs"
              >
                {{ props.visit.tracking_device?.drivers?.[0]?.name }}
              </span>
            </template>
            <template v-if="props.visit.tracking_device?.license_plate">
              <span>•</span>
              <span
                v-tooltip="
                  `License Plate: ${props.visit.tracking_device?.license_plate}`
                "
                class="text-gray-500 text-xs"
              >
                {{ props.visit.tracking_device?.license_plate }}
              </span>
            </template>
          </span>
        </div>
        <div class="flex items-center space-x-2">
          <div class="flex items-center space-x-1">
            <span
              class="text-sm"
              :class="props.visit.departure ? 'text-gray-900' : 'text-blue-600'"
            >
              {{ props.visit.departure ? 'Completed in' : 'In Progress for' }}
              <span
                class="font-medium mr-[3px]"
                :class="getDurationColor(props.visit)"
              >
                {{
                  calculateDuration(props.visit.arrival, props.visit.departure)
                }}
              </span>
            </span>
            <component
              :is="props.visit.departure ? CompletedIcon : InProgressIcon"
              class="w-4 h-4"
              :class="
                props.visit.departure ? 'text-green-600' : 'text-blue-600'
              "
            />
          </div>
        </div>
      </div>

      <!-- Second Row -->
      <div class="flex items-center justify-between mt-1">
        <div class="flex items-center space-x-4">
          <div class="flex items-center space-x-3 text-sm text-gray-600">
            <span
              >Date:
              {{ DateTime.fromISO(props.visit.arrival).toFormat('M/d') }}</span
            >
            <!-- Container Information -->
            <div
              v-if="extractedContainers.length > 0"
              class="flex items-center space-x-1"
            >
              <div
                class="flex items-center space-x-1 bg-gray-100 rounded px-1 py-0.5"
              >
                <!-- Empty Containers (Drop off) -->
                <template v-if="hasEmptyContainers">
                  <template
                    v-for="(container, index) in emptyContainers.slice(0, 2)"
                    :key="`empty-${container.number}-${index}`"
                  >
                    <span v-if="index > 0" class="text-xs text-gray-400"
                      >/</span
                    >
                    <div class="flex items-center space-x-1">
                      <ClickToCopy
                        :text-to-copy="container.number"
                        class="text-xs"
                      >
                        {{ container.number }}
                      </ClickToCopy>
                      <EmptyContainerIcon
                        class="w-3 h-3 text-gray-400"
                        title="Empty"
                      />
                    </div>
                  </template>
                  <template v-if="emptyContainers.length > 2">
                    <span class="text-xs text-gray-400">/</span>
                    <span
                      class="text-xs text-gray-500"
                      :title="`${emptyContainers.length - 2} more empty containers`"
                    >
                      +{{ emptyContainers.length - 2 }}
                    </span>
                  </template>
                </template>
                <template v-else>
                  <span class="text-xs text-gray-400">No Drop off</span>
                </template>

                <!-- Separator -->
                <span class="text-xs text-gray-400">/</span>

                <!-- Loaded Containers (Pick up) -->
                <template v-if="hasLoadedContainers">
                  <template
                    v-for="(container, index) in loadedContainers.slice(0, 2)"
                    :key="`loaded-${container.number}-${index}`"
                  >
                    <span v-if="index > 0" class="text-xs text-gray-400"
                      >/</span
                    >
                    <div class="flex items-center space-x-1">
                      <ClickToCopy
                        :text-to-copy="container.number"
                        class="text-xs"
                      >
                        {{ container.number }}
                      </ClickToCopy>
                      <LoadedContainerIcon
                        class="w-3 h-3 text-gray-700"
                        title="Loaded"
                      />
                    </div>
                  </template>
                  <template v-if="loadedContainers.length > 2">
                    <span class="text-xs text-gray-400">/</span>
                    <span
                      class="text-xs text-gray-500"
                      :title="`${loadedContainers.length - 2} more loaded containers`"
                    >
                      +{{ loadedContainers.length - 2 }}
                    </span>
                  </template>
                </template>
                <template v-else>
                  <span class="text-xs text-gray-400">No Pick up</span>
                </template>
              </div>
            </div>
            <!-- Missing Transaction Badge -->
            <el-tag
              v-if="hasMissingTransaction"
              type="warning"
              size="small"
              effect="plain"
              class="!text-xs !px-2 !py-0"
            >
              Missing Transaction
            </el-tag>
          </div>
        </div>
        <div class="flex items-center justify-end text-sm">
          <div class="flex items-center space-x-1 text-gray-600">
            <div
              v-tooltip="'Line Time'"
              class="flex items-center space-x-1 w-18"
            >
              <TruckIcon class="w-4 h-4 ml-auto" />
              <span>{{ formatTime(props.visit.line) }}</span>
            </div>
            <div
              v-tooltip="'Entry Time'"
              class="flex items-center space-x-1 w-18"
            >
              <InGateIcon class="w-4 h-4 ml-auto" />
              <span>{{ formatTime(props.visit.arrival) }}</span>
            </div>
            <div
              v-tooltip="'Exit Time'"
              class="flex items-center space-x-1 w-18"
            >
              <OutGateIcon class="w-4 h-4 ml-auto" />
              <span>{{ formatTime(props.visit.departure) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
